import { css } from '@styled-system/css';
import { ImagePlus, Trash2 } from 'lucide-react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@/shared/components/ui/Accordion';
import { Button } from '@/shared/components/ui/Button';
import { FormField } from '@/shared/components/ui/FormField';
import { Spinner } from '@/shared/components/ui/Spinner';
import { ACCEPTED_IMAGE_TYPES, type UploadedSearchImage } from '@/shared/utils/searchImageUpload';

// ============================================================================
// Section « Recherche par image » de SearchModal — PARTAGÉE par l'onglet Vinted et le
// panneau Vestiaire (mêmes blocs, mêmes textes) : article source d'une relance « articles
// similaires », image d'une recherche existante (édition), image téléversée, zone d'envoi,
// description sémantique. L'état (image, envoi, description) reste dans SearchModal.
// ⚠️ Rend un `Accordion.Item` : à placer DANS un `<Accordion>`.
// ============================================================================

export interface SimilaritySourceProduct {
  title: string;
  imageUrl?: string | null;
}

export interface SimilaritySearchSectionProps {
  searchImage: UploadedSearchImage | null;
  imageUploading: boolean;
  onImageSelect: (file: File | undefined) => void;
  onRemoveImage: () => void;
  description: string;
  onDescriptionChange: (value: string) => void;
  /** Mode édition : image téléversée de la recherche existante. */
  editImageKey: string | null;
  editImageUrl: string | null;
  /** Mode édition d'une relance « articles similaires » : article source. */
  editSourceProductId: string | null;
  /** Article source résolu (Vinted seulement : `getProductById`) — absent ⇒ vignette de l'analyse. */
  sourceProduct?: SimilaritySourceProduct | null;
  initialImageUrl: string | null;
}

export const SimilaritySearchSection: FC<SimilaritySearchSectionProps> = ({
  searchImage,
  imageUploading,
  onImageSelect,
  onRemoveImage,
  description,
  onDescriptionChange,
  editImageKey,
  editImageUrl,
  editSourceProductId,
  sourceProduct,
  initialImageUrl,
}) => {
  const { t } = useTranslation();
  // Section dédiée, au même titre que Catégories/Marques : photo téléversée + description
  // sémantique (combinables aux critères classiques, jamais suffisantes seules).
  return (
    <Accordion.Item value="image">
      <Accordion.Header
        icon={<ImagePlus size={16} strokeWidth={1.75} />}
        label={t('searchModal.imageSearch')}
        count={(searchImage || editImageKey || editSourceProductId ? 1 : 0) + (description.trim() ? 1 : 0)}
      />
      <Accordion.Content>
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '4' })}>
          {editSourceProductId ? (
            // Relance « articles similaires » : la requête visuelle est
            // la photo (+ texte) de l'article source. L'article peut
            // avoir été purgé de la table live (fenêtre glissante v2)
            // -> repli sur la vignette de l'analyse.
            <div className={css({ display: 'flex', alignItems: 'center', gap: '4' })}>
              {(sourceProduct?.imageUrl ?? initialImageUrl) && (
                <img
                  src={sourceProduct?.imageUrl ?? initialImageUrl ?? undefined}
                  alt={sourceProduct?.title ?? t('searchModal.sourceItemAlt')}
                  className={css({
                    width: '72px',
                    height: '72px',
                    objectFit: 'cover',
                    borderRadius: 'md',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'border.default',
                    bg: 'surface.sunken',
                  })}
                />
              )}
              <div className={css({ display: 'flex', flexDirection: 'column', gap: '1' })}>
                <span className={css({ fontSize: 'caption', fontWeight: 'medium', color: 'text.primary' })}>
                  {t('searchModal.basedOnSource')}
                  {sourceProduct ? ` : ${sourceProduct.title}` : ''}
                </span>
                <span className={css({ fontSize: 'micro', color: 'text.tertiary' })}>
                  {t('searchModal.mergedSearchHint')}
                </span>
              </div>
            </div>
          ) : editImageKey ? (
            // Mode édition : aperçu si l'URL est connue localement
            // (upload fait sur ce navigateur), indicateur sinon
            editImageUrl ? (
              <div className={css({ display: 'flex', alignItems: 'center', gap: '4' })}>
                <img
                  src={editImageUrl}
                  alt={t('searchModal.searchImageAlt')}
                  className={css({
                    width: '72px',
                    height: '72px',
                    objectFit: 'cover',
                    borderRadius: 'md',
                    borderWidth: '1px',
                    borderStyle: 'solid',
                    borderColor: 'border.default',
                    bg: 'surface.sunken',
                  })}
                />
                <span className={css({ fontSize: 'caption', color: 'text.secondary' })}>
                  {t('searchModal.referenceImage')}
                </span>
              </div>
            ) : (
              <div
                className={css({
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3',
                  paddingY: '3',
                  paddingX: '4',
                  borderRadius: 'md',
                  bg: 'info.soft',
                  color: 'info.default',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'info.border',
                  fontSize: 'caption',
                  fontWeight: 'medium',
                })}
              >
                <ImagePlus size={14} strokeWidth={1.75} />
                {t('searchModal.referenceImageIndicator')}
              </div>
            )
          ) : searchImage ? (
            <div className={css({ display: 'flex', alignItems: 'center', gap: '4' })}>
              <img
                src={searchImage.publicUrl}
                alt={t('searchModal.searchImageAlt')}
                className={css({
                  width: '72px',
                  height: '72px',
                  objectFit: 'cover',
                  borderRadius: 'md',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: 'border.default',
                  bg: 'surface.sunken',
                })}
              />
              <div
                className={css({
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2',
                  alignItems: 'flex-start',
                })}
              >
                <span className={css({ fontSize: 'caption', color: 'text.secondary' })}>
                  {t('searchModal.imageMatchHint')}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  icon={<Trash2 size={12} strokeWidth={1.75} />}
                  onClick={onRemoveImage}
                >
                  {t('searchModal.removeImage')}
                </Button>
              </div>
            </div>
          ) : (
            <label
              className={css({
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '2',
                paddingY: '6',
                paddingX: '4',
                borderWidth: '1px',
                borderStyle: 'dashed',
                borderColor: 'border.strong',
                borderRadius: 'md',
                cursor: imageUploading ? 'wait' : 'pointer',
                color: 'text.tertiary',
                transition: 'border-color 120ms ease, color 120ms ease',
                _hover: { borderColor: 'brand.primary', color: 'text.secondary' },
              })}
            >
              {imageUploading ? <Spinner size={16} /> : <ImagePlus size={18} strokeWidth={1.5} />}
              <span className={css({ fontSize: 'caption', fontWeight: 'medium' })}>
                {imageUploading ? t('searchModal.uploading') : t('searchModal.choosePhoto')}
              </span>
              <span className={css({ fontSize: 'micro' })}>{t('searchModal.imageFormats')}</span>
              <input
                type="file"
                accept={ACCEPTED_IMAGE_TYPES.join(',')}
                disabled={imageUploading}
                onChange={(e) => {
                  onImageSelect(e.target.files?.[0]);
                  e.target.value = '';
                }}
                className={css({ display: 'none' })}
              />
            </label>
          )}

          <FormField label={t('searchModal.descriptionLabel')} hint={t('searchModal.descriptionHint')}>
            <textarea
              value={description}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder={t('searchModal.descriptionPlaceholder')}
              rows={3}
              className={css({
                width: '100%',
                resize: 'vertical',
                paddingY: '2.5',
                paddingX: '3.5',
                bg: 'surface.default',
                borderWidth: '1px',
                borderStyle: 'solid',
                borderColor: 'border.default',
                borderRadius: 'md',
                fontSize: 'bodySm',
                fontFamily: 'sans',
                color: 'text.primary',
                outline: 'none',
                transition: 'border-color 120ms ease',
                _focus: { borderColor: 'brand.primary' },
                _placeholder: { color: 'text.tertiary' },
              })}
            />
          </FormField>
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
};
